"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const solutions = [{
  id: "01",
  title: "Retail & E-commerce",
  description: "Optimize supply chains and personalize shopper experiences with Zemio’s proprietary AI integration. We transform traditional retail data into actionable insights that drive conversion and customer loyalty.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633756/img/retail_ai_automation.png",
  benefits: ["Predictive inventory and demand forecasting", "Personalized customer journey mapping", "Automated visual merchandising and pricing"]
}, {
  id: "02",
  title: "Healthcare Systems",
  description: "Enhance diagnostic accuracy and streamline patient data management. Our AI solutions provide HIPAA-compliant processing with unmatched clinical efficiency, allowing providers to focus on care.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633756/img/healthcare_ai_diagnostics.png",
  benefits: ["AI-driven clinical decision support", "Automated medical coding and billing", "Predictive patient health monitoring"]
}];
export default function IndustrySolutions() {
  return <section data-section-id="99055" id="industry-verticals" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-primary/5 blur-[120px] rounded-full" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-20 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tight mb-6">
            Industry <span className="text-primary">Verticals</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Tailored AI solutions for high-impact sectors. Zemio combines deep technical expertise with industry-specific insights to drive measurable ROI for small and medium businesses.
          </p>
        </div>

        <div className="flex flex-col gap-24 lg:gap-40">
          {solutions.map((solution, i) => {
          const isEven = i % 2 === 0;
          const {
            ref,
            isVisible
          } = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.1
          });
          return <div key={solution.id} data-index={i} ref={ref} className={`relative grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ease-out p-8 border border-primary/20 rounded-lg hover:bg-accent/10 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                {}
                <div className={`absolute top-1/2 -translate-y-1/2 text-[150px] md:text-[250px] font-default font-bold leading-none select-none pointer-events-none z-0
                    text-transparent [-webkit-text-stroke:1px_hsl(var(--primary))] opacity-10
                    ${isEven ? "left-0 lg:-left-10" : "right-0 lg:-right-10"}
                  `} aria-hidden="true">
                  {solution.id}
                </div>

                {}
                <div className={`relative z-10 w-full aspect-[4/3] lg:aspect-square rounded-lg overflow-hidden border border-primary/20 bg-muted/20 shadow-sm shadow-primary/5 ${isEven ? "lg:order-1" : "lg:order-2"}`}>
                  <div className="absolute inset-0 bg-gradient-to-tr from-background/40 via-transparent to-transparent z-10" />
                  <img src={solution.image} alt={`${solution.title} AI integration by Zemio`} className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-105" />
                </div>

                {}
                <div className={`relative z-10 flex flex-col justify-center ${isEven ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary text-sm font-default font-medium w-fit mb-6 border border-primary/20">
                    <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    Vertical {solution.id}
                  </div>
                  
                  <h3 className="text-3xl md:text-4xl font-default font-bold mb-6 text-foreground">
                    {solution.title}
                  </h3>
                  
                  <p className="text-lg text-muted-foreground mb-8 leading-relaxed font-default">
                    {solution.description}
                  </p>

                  <Separator className="mb-8 bg-primary/20" />

                  <ul className="space-y-4 mb-10">
                    {solution.benefits.map((benefit, index) => <li key={index} data-index={index} className="flex items-start gap-3">
                        <CircleCheck className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                        <span className="text-foreground/90 font-default">{benefit}</span>
                      </li>)}
                  </ul>

                  <div>
                    <Button size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-default">
                      View {solution.title.split(' ')[0]} Case Study
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}